import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "src/environments/environment";
import { ActiveKYCMode, RegisterByPan, SentOtpOfLogin, VerifyOtpOfLogin, userRedirect } from "./auth.interface";
import { StoreService } from "../shared/services/store.service";
import { Router } from "@angular/router";
import { LoaderService } from "../shared/services/loader.service";
import { catchError, finalize, from, map, Observable, of, switchMap } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { MatDialogSizeService } from "../shared/services/mat-dialog-size";
import { FlyyWebSDKService } from "../shared/services/flyy-web-sdk.service";
import FlyySDK from "flyy-web-sdk";
import { GtmService } from "../shared/services/gtm-service/gtm.service";
import { NetCoreService } from "../shared/services/netcore/netcore.service";
import { UserPropertiesService } from "../shared/services/user-services/user-properties.service";
import { GlobalService } from "../shared/services/global.service";
import { LogoutService } from "../shared/services/logout.service";

@Injectable({
    providedIn: "root",
})
export class AuthService {
    baseUrl = `${environment.baseUrl}`;
    flysdd: any;

    // Set by LoginOtpComponent when OTP succeeds and the browser has not granted location, so the
    // permission screen is shown before the normal redirects. Cleared by PermissionComponent.
    showPermissionAfterOtp = false;

    constructor(
        private http: HttpClient,
        private storeService: StoreService,
        private router: Router,
        private loaderService: LoaderService,
        private dialog: MatDialog,
        private matDialogSizeService: MatDialogSizeService,
        private flyyWebSDKService: FlyyWebSDKService,
        private netCoreService: NetCoreService,
        private userPropertiesService: UserPropertiesService,
        private globalService: GlobalService,
        private logoutService: LogoutService,
        private gtmService: GtmService
    ) {
        this.flysdd = new FlyySDK();
    }

    sentOtpOfLogin(req: SentOtpOfLogin) {
        return this.http.post<any>(`${this.baseUrl}/auth/mobile/otp/sent`, req);
    }

    verifyOtpOfLogin(req: VerifyOtpOfLogin) {
        return this.http.post<any>(`${this.baseUrl}/auth/mobile/otp/verify`, req);
    }

    selectedAAdhar(customerId: string) {
        return this.http.post<any>(`${this.baseUrl}/auth/customer/select/aadhar`, {
            customerId: customerId,
        });
    }

    getRegisterProfile() {
        return this.http.get<any>(`${this.baseUrl}/auth/customer/details/get`);
    }

    refreshToken(refreshToken: string) {
        return this.http.post<any>(`${this.baseUrl}/auth/generate/access`, {
            refreshToken: refreshToken,
        });
    }

    registerByPan(req: RegisterByPan) {
        return this.http.post<any>(`${this.baseUrl}/auth/customer/register/v2/by-pan`, req);
    }

    getPermissionList() {
        return this.http.get<any>(`${this.baseUrl}/app-permission/list`);
    }

    getSalaryRange() {
        return this.http.get<any>(`${this.baseUrl}/auth/salary-range/list`);
    }

    userRedirect(req: userRedirect) {
        let params = new HttpParams();
        params = params.set("comeFromGoogleOrDirect", true);
        Object.keys(req || {}).forEach((key) => {
            const value = req[key];
            if (value !== undefined && value !== null && `${value}` !== "") {
                params = params.set(key, `${value}`);
            }
        });
        // Pass the `params` object as part of the HTTP request options
        return this.http.get<any>(`${this.baseUrl}/user/track`, { params });
    }

    loadOnboardingPages(stag: any) {
        let isPermission = this.storeService.get("permission");
        if (!isPermission) {
            this.router.navigate(["/auth/permission"]);
            return;
        }

        let employedType = this.storeService.get("employedType");
        if (!employedType) {
            this.router.navigate(["/auth/salaried-or-self-employed"]);
            return;
        }

        let monthlyIncome = this.storeService.get("monthlyIncome");
        if (!monthlyIncome) {
            this.router.navigate(["/auth/monthly-income"]);
            return;
        }

        if (stag == "PROFILE") {
            this.router.navigate(["/auth/register"]);
            return;
        }

        this.router.navigate(["/auth/pan"]);
        return;
    }

    // Flyy's startReferralTracking() copies the link's referral param into this cookie on app load.
    extractReferralCodeFromCookie(): string | null {
        try {
            const parts = `; ${document.cookie}`.split(`; flyy_utm_referrer=`);
            if (parts.length === 2) {
                const value = parts.pop()?.split(";").shift() ?? null;
                if (value && value !== "null") {
                    return value.split("~")[0];
                }
            }
        } catch (error) {
            console.error("Error extracting referral code from cookie:", error);
        }
        return null;
    }

    validation() {
        let getAuthDetails: any = this.storeService.get("unAuthDetails");
        getAuthDetails = JSON.parse(getAuthDetails || "{}");

        if (getAuthDetails && getAuthDetails?.activeKYCMode == ActiveKYCMode.NEW_MODE) {
            // Register flow: ask for a referral code right after permissions, once.
            const isExistingCustomer = !!getAuthDetails?.user?.[0]?.customerId;
            if (!isExistingCustomer && !this.storeService.get("referralSeen")) {
                if (!this.storeService.get("permission")) {
                    this.router.navigate(["/auth/permission"]);
                    return;
                }

                // A referral link already carries the code: store it and skip the screen.
                const cookieReferral = this.extractReferralCodeFromCookie();
                if (cookieReferral) {
                    this.storeService.set({ key: "referralCode", value: cookieReferral });
                    this.storeService.set({ key: "referralSeen", value: "true" });
                } else {
                    this.router.navigate(["/auth/referral"]);
                    return;
                }
            }

            if (getAuthDetails?.stage == "EMAIL_VERIFICATION") {
                let isPermission = this.storeService.get("permission");
                if (!isPermission) {
                    this.router.navigate(["/auth/permission"]);
                    return;
                }

                this.router.navigate(["/auth/email-verify"]);
                return;
            } else if (getAuthDetails?.stage == "PAN_PRO_VERIFICATION") {
                if (
                    getAuthDetails?.user != undefined &&
                    getAuthDetails?.user?.length > 0 &&
                    getAuthDetails?.user?.[0]?.customerId
                ) {
                    this.selectedAAdharList(getAuthDetails?.user?.[0]?.customerId);
                } else {
                    this.loadOnboardingPages(getAuthDetails?.stage);
                }
            } else if (getAuthDetails?.stage == "PROFILE") {
                this.loadOnboardingPages(getAuthDetails?.stage);
            }
        } else {
            this.router.navigate(["/auth/login"]);
        }
    }

    selectedAAdharList(customerId: number) {
        const sub = this.selectedAAdhar(customerId?.toString())
            .pipe(
                map(async (res: any) => {
                    if (res.success) {
                        this.storeService.set({
                            key: "token",
                            value: res.data.accessToken,
                        });

                        this.storeService.set({
                            key: "refreshToken",
                            value: res.data.refreshToken,
                        });

                        res.data.customerDetails.customerProfile = await this.globalService.getBlobFromUrl(
                            res.data.customerDetails.customerProfile
                        );

                        this.storeService.set({
                            key: "employmentStatus",
                            value: res.data.customerDetails.employmentStatus,
                        });

                        this.storeService.set({
                            key: "customerDetails",
                            value: JSON.stringify(res.data.customerDetails),
                        });

                        this.userPropertiesService.updateUserProperty("mobile", res.data.customerDetails.mobile);
                        // set here user salary range
                        this.gtmService.refreshUserProperties();

                        this.globalService.customerDetails.next(res.data.customerDetails);
                        this.netCoreService.trackUserProperties({
                            "pk^mobile": res.data.customerDetails.mobile,
                            email: res.data.customerDetails.email,
                            NAME: res.data.customerDetails.name,
                            mobile: res.data.customerDetails.mobile,
                            AGE: this.userPropertiesService.calculateAge(res.data.customerDetails.dob),
                            CUSTOMER_ID: res.data.customerDetails.customerId,
                            GENDER: this.userPropertiesService.mapGender(res.data.customerDetails.gender),
                            MARITAL_STATUS: this.userPropertiesService.normalizeMaritalStatus(
                                res.data.customerDetails.maritalStatus
                            ),
                            CREDIT_RANGE: this.userPropertiesService.getCreditRange(
                                res.data.customerDetails.creditScore
                            ),
                            USER_TYPE: this.userPropertiesService.getUserType(res.data.customerDetails.loanCount),
                            USER_CITY: res.data.customerDetails.city,
                        });

                        // Set user ID in GTM dataLayer
                        this.gtmService.setUserId(res.data.customerDetails.customerId?.toString());
                        this.userPropertiesService.updateUserProperty("customerName", res.data.customerDetails.name);
                        this.gtmService.refreshUserProperties();

                        // need to send here GA4 event
                        this.gtmService.trackConfigEvent("LOGIN", {
                            age: this.userPropertiesService.calculateAge(res.data.customerDetails.dob),
                            customer_id: res.data.customerDetails.customerId,
                            gender: this.userPropertiesService.mapGender(res.data.customerDetails.gender),
                            marital_status: this.userPropertiesService.normalizeMaritalStatus(
                                res.data.customerDetails.maritalStatus
                            ),
                            user_type: this.userPropertiesService.getUserType(res.data.customerDetails.loanCount),
                            user_city: res.data.customerDetails.city,
                            mobile: res.data.customerDetails.mobile,
                        });

                        // this.loanDashboardService.getDashboardDetails();
                        this.logoutService.globalChannel.postMessage({
                            source: "LOGIN",
                        });

                        if (this.storeService.get("stage") === "cibil") {
                            this.storeService.set({
                                key: "fromD2cDetails",
                                value: "DASHBOARD",
                            });
                            this.router.navigate(["/d2c"]);
                            return;
                        }

                        this.router.navigate(["/loan/timeline"]);
                    }
                })
            )
            .subscribe({
                next: () => {},
                error: () => {
                    sub.unsubscribe();
                },
                complete: () => {
                    sub.unsubscribe();
                },
            });
    }

    getRegisterProfileDetails(onlyGetDetails: boolean = false): Observable<any> {
        let getAuthDetails: any = this.storeService.get("unAuthDetails");
        getAuthDetails = JSON.parse(getAuthDetails || "{}");
        if (!getAuthDetails || getAuthDetails?.stage != "PROFILE") {
            this.validation();
            return of({});
        } else {
            return this.getRegisterProfile() // <-- your actual API call
                .pipe(
                    map((res: any) => {
                        if (onlyGetDetails) {
                            return res?.data || {};
                        }

                        if (res.success) {
                            const details = Object.keys(res?.data).length > 0 ? res?.data : {};
                            this.storeService.set({
                                key: "notRegDetails",
                                value: JSON.stringify(details),
                            });

                            if (getAuthDetails && getAuthDetails?.activeKYCMode == ActiveKYCMode.NEW_MODE) {
                                this.router.navigate(["/loan/apply"]);
                            }

                            return details;
                        }
                        return {};
                    }),
                    finalize(() => this.loaderService.hide())
                );
        }
    }

    userRegisterByPan(): Observable<boolean> {
        const referralCode: string = this.storeService.get("referralCode")?.toString() || "";
        const employmentStatus: string = this.storeService.get("employedType")?.toString() || "";
        const salaryRange: string = this.storeService.get("monthlyIncome")?.toString() || "";

        if (!employmentStatus) {
            this.router.navigate(["/auth/salaried-or-self-employed"]);
            return of(false);
        }

        if (!salaryRange) {
            this.router.navigate(["/auth/monthly-income"]);
            return of(false);
        }

        return this.registerByPan({
            referralCode,
            employmentStatus,
            salaryRange,
        }).pipe(
            switchMap((res: any) => {
                if (!res?.success) {
                    throw new Error(res?.message || "User registration failed");
                }

                return from(
                    (async () => {
                        // ---- Flyy integration ----
                        if (referralCode && res.data?.flyyData?.token && res.data?.flyyData?.device_id) {
                            const d = new Date();
                            d.setTime(d.getTime() + 24 * 60 * 60 * 1000);

                            document.cookie = `flyy_utm_referrer=${referralCode}; expires=${d.toUTCString()}; path=/`;

                            this.storeService.set({ key: "flyyToken", value: res.data.flyyData.token });
                            this.storeService.set({ key: "flyyDeviceId", value: res.data.flyyData.device_id });

                            this.flyyWebSDKService.initializeFlyySDK(res.data.flyyData.token, res.data.customerName);

                            this.flysdd.startReferralTracking();
                        }

                        const { accessToken, refreshToken, customerDetails } = res.data;

                        this.netCoreService.trackUserProperties({
                            "pk^mobile": customerDetails.mobile,
                            email: customerDetails.email,
                            NAME: customerDetails.name,
                            mobile: customerDetails.mobile,
                            AGE: this.userPropertiesService.calculateAge(customerDetails.dob),
                            CUSTOMER_ID: customerDetails.customerId,
                            GENDER: this.userPropertiesService.mapGender(customerDetails.gender),
                            MARITAL_STATUS: this.userPropertiesService.normalizeMaritalStatus(
                                customerDetails.maritalStatus
                            ),
                            CREDIT_RANGE: this.userPropertiesService.getCreditRange(customerDetails.creditScore),
                            USER_TYPE: this.userPropertiesService.getUserType(customerDetails.loanCount),
                            USER_CITY: customerDetails.city,
                        });
                        // adding here new register event for GA4
                        this.gtmService.trackConfigEvent("REGISTER", {
                            age: this.userPropertiesService.calculateAge(customerDetails.dob),
                            customer_id: customerDetails.customerId,
                            gender: this.userPropertiesService.mapGender(customerDetails.gender),
                            marital_status: this.userPropertiesService.normalizeMaritalStatus(
                                customerDetails.maritalStatus
                            ),
                            user_type: this.userPropertiesService.getUserType(customerDetails.loanCount),
                            user_city: customerDetails.city,
                            mobile: customerDetails.mobile,
                        });

                        // ---- Tokens ----
                        this.storeService.set({ key: "token", value: accessToken });
                        this.storeService.set({ key: "refreshToken", value: refreshToken });
                        this.storeService.set({
                            key: "monthlyIncome",
                            value: customerDetails?.salaryRange?.toString(),
                        });
                        // ---- Async profile image ----
                        customerDetails.customerProfile = await this.globalService.getBlobFromUrl(
                            customerDetails.customerProfile
                        );

                        this.storeService.set({
                            key: "customerDetails",
                            value: JSON.stringify(customerDetails),
                        });

                        this.globalService.customerDetails.next(customerDetails);

                        this.gtmService.setUserId(customerDetails.customerId?.toString());
                        this.userPropertiesService.updateUserProperty("customerName", customerDetails.name);
                        this.userPropertiesService.updateUserProperty("mobile", customerDetails.mobile);
                        // set here user salary range

                        this.gtmService.refreshUserProperties();

                        // ---- Action handling ----
                        const actionType = customerDetails?.actionType;

                        if (actionType === "COOL_OFF") {
                            this.router.navigate(["/auth/register/info"], {
                                state: {
                                    icon: "assets/svg/icons/cross-circle-gb.svg",
                                    title: `You’ll be eligible to apply once you turn ${customerDetails.minAge}.`,
                                    rightButtonName: "Go to Dashboard",
                                    rightButtonRoute: "/loan/timeline",
                                },
                            });
                            return false;
                        }

                        if (actionType === "BLACK_LIST") {
                            this.router.navigate(["/auth/register/info"], {
                                state: {
                                    icon: "assets/svg/icons/cross-circle-gb.svg",
                                    title: `You’re not eligible for a Klinq loan as the age limit is ${customerDetails.maxAge}.`,
                                    description: "Thanks for taking the time to explore Klinq.",
                                    rightButtonName: "Go to Dashboard",
                                    rightButtonRoute: "/loan/timeline",
                                },
                            });
                            return false;
                        }

                        return true;
                    })()
                );
            }),
            catchError(() => {
                return of(false);
            })
        );
    }
}
